import { AxiosInstance, AxiosResponse } from "axios";

/**
 * List the tenant's Extended PoPs: one row per site (85-), each with its
 * CPEs (80-/83-) under it, active first.
 */
export function fetchExtenderPopList(
  api: AxiosInstance,
  tenantId: number
): Promise<AxiosResponse> {
  return api.get(
    `/kontroledge/extender-pops?tenantId=${tenantId}&includeChildServices=true`
  );
}

/**
 * Detail of one Extended PoP: the site with its CPEs, each with its role,
 * links, carrier legs and subscription term.
 */
export function fetchExtenderPop(
  api: AxiosInstance,
  extenderPopId: number,
  tenantId: number
): Promise<AxiosResponse> {
  return api.get(
    `/kontroledge/extender-pops/${extenderPopId}?tenantId=${tenantId}`
  );
}

/**
 * The logical services the Extended PoP delivers (Internet, DCI, Cloud,
 * Firewall), each with its port, CPE and live status (CTX-797).
 */
export function fetchExtenderPopConnectionsAPI(
  api: AxiosInstance,
  extenderPopId: number,
  tenantId: number
): Promise<AxiosResponse> {
  return api.get(
    `/kontroledge/extender-pops/${extenderPopId}/connections?tenantId=${tenantId}`
  );
}

/**
 * Bandwidth utilization for ONE link of the Extender PoP (a backbone
 * uplink or the HA interconnect), selected by `linkId` from the detail's
 * `links`. Same query modes as the Ports performance endpoint.
 */
export function fetchExtenderPopPerformanceAPI(
  api: AxiosInstance,
  extenderPopId: string,
  linkId: string,
  interval: string,
  tenantId: number,
  timeRange?: string,
  startDate?: string,
  endDate?: string,
  maxPoints?: number
): Promise<AxiosResponse> {
  const params = new URLSearchParams({
    tenantId: String(tenantId),
    linkId,
    interval,
  });
  if (startDate && endDate) {
    params.set("start", startDate);
    params.set("end", endDate);
  } else if (timeRange) {
    params.set("timeRange", timeRange);
  }
  if (maxPoints) {
    params.set("maxPoints", String(maxPoints));
  }
  return api.get(
    `/kontroledge/extender-pops/${extenderPopId}/performance?${params.toString()}`
  );
}

/** A leg declared by hand on an interface of a CPE's router (CTX-830). */
export interface ManualLegBody {
  cpeId: number;
  portAllocationId: number;
  carrierCode: string;
  circuitId?: string;
  serviceRequestId?: string;
  medium?: "fiber" | "satellite";
}

/**
 * Declare the Carrier Access (84-) of the site and claim its legs. Global
 * Admin only. `legIds` left out claims every free leg of the site.
 */
export function declareExtenderPopCarrierAccessAPI(
  api: AxiosInstance,
  extenderPopId: number,
  tenantId: number,
  body: {
    name?: string;
    accessType?: string;
    legIds?: number[];
    manualLegs?: ManualLegBody[];
  }
): Promise<AxiosResponse> {
  return api.post(
    `/kontroledge/extender-pops/${extenderPopId}/carrier-access?tenantId=${tenantId}`,
    body
  );
}

/** Claim a derived leg (`{ legId }`) or add a manual one. Global Admin only. */
export function addExtenderPopCarrierAccessLegAPI(
  api: AxiosInstance,
  extenderPopId: number,
  carrierAccessId: number,
  tenantId: number,
  body: { legId: number } | ManualLegBody
): Promise<AxiosResponse> {
  return api.post(
    `/kontroledge/extender-pops/${extenderPopId}/carrier-access/${carrierAccessId}/legs?tenantId=${tenantId}`,
    body
  );
}

/** Delete a manual leg or release a derived one. Global Admin only. */
export function removeExtenderPopCarrierAccessLegAPI(
  api: AxiosInstance,
  extenderPopId: number,
  carrierAccessId: number,
  legId: number,
  tenantId: number
): Promise<AxiosResponse> {
  return api.delete(
    `/kontroledge/extender-pops/${extenderPopId}/carrier-access/${carrierAccessId}/legs/${legId}?tenantId=${tenantId}`
  );
}

/** Live interfaces of a CPE's router, to place a manual leg. Global Admin only. */
export function fetchExtenderPopCpeInterfacesAPI(
  api: AxiosInstance,
  extenderPopId: number,
  cpeId: number,
  tenantId: number
): Promise<AxiosResponse> {
  return api.get(
    `/kontroledge/extender-pops/${extenderPopId}/cpes/${cpeId}/interfaces?tenantId=${tenantId}`
  );
}

/**
 * Capture the medium of a carrier leg of the site: fiber, satellite, or null
 * to clear it (CTX-800). Global Admin only.
 */
export function setExtenderPopLegMediumAPI(
  api: AxiosInstance,
  extenderPopId: number,
  legId: number,
  tenantId: number,
  medium: "fiber" | "satellite" | null
): Promise<AxiosResponse> {
  return api.patch(
    `/kontroledge/extender-pops/${extenderPopId}/legs/${legId}?tenantId=${tenantId}`,
    { medium }
  );
}
